// Centralized REST client for online mode - see server/server.js's "Online
// mode REST API" section. Event mode never uses this file; it stays entirely
// on the websocket (network.js). Session token lives under localStorage's
// "online_session" key, deliberately separate from the "session" key
// network.js reads for its websocket CLIENT_JOIN handshake, so an online
// login can never accidentally get replayed as a websocket session.
const MEDIA_CHUNK_SIZE = 5 * 1024 * 1024;
const MEDIA_CHUNK_RETRIES = 5;

const api = {
    // See network.js's matching VUE_APP_WS_SAME_ORIGIN branch - a tunnel
    // (ngrok) only exposes one origin/port, proxied by vue.config.js's
    // devServer.proxy to this same /api path on the real API_PORT.
    base_url(){
        if(process.env.VUE_APP_WS_SAME_ORIGIN === 'true')
            return window.location.origin + '/api';

        const protocol = (process.env.NODE_ENV === 'development') ? 'http://' : 'https://';
        const host = process.env.VUE_APP_HOST || window.location.hostname;
        const port = process.env.VUE_APP_API_PORT;
        return protocol + host + ':' + port + '/api';
    },
    token(){
        return localStorage.getItem('online_session');
    },
    async request(method, path, body, auth){
        const headers = { 'Content-Type': 'application/json' };
        if(auth !== false){
            const token = api.token();
            if(token)
                headers['Authorization'] = 'Bearer ' + token;
        }

        const response = await fetch(api.base_url() + path, {
            method: method,
            headers: headers,
            body: body ? JSON.stringify(body) : undefined
        });
        const result = await response.json();

        if(!response.ok || result.ok != true){
            const error = new Error(result.message || 'No se ha podido completar la solicitud.');
            error.status = response.status;
            error.payload = result;
            throw error;
        }

        return result;
    },
    // Public - no session, called at boot before any identity exists yet
    // (see main.js). The server is the single source of truth for MODE, not
    // a client-side build-time env var, which otherwise has to be hand-kept
    // in sync and silently drifts (see server.js's GET /api/mode).
    mode(){
        return api.request('GET', '/mode', null, false);
    },
    register(name, email, password){
        return api.request('POST', '/register', { name: name, email: email, password: password }, false);
    },
    login(email, password){
        return api.request('POST', '/login', { email: email, password: password }, false);
    },
    me(){
        return api.request('GET', '/me');
    },
    seasons(){
        return api.request('GET', '/seasons');
    },
    stages(season_id){
        return api.request('GET', '/seasons/' + season_id + '/stages');
    },
    stage_questions(stage_id){
        return api.request('GET', '/stages/' + stage_id + '/questions');
    },
    submit_answer(question_id, choice, timestamp){
        return api.request('POST', '/answers', { question_id: question_id, choice: choice, timestamp: timestamp });
    },
    // Public - looked up before the caller necessarily has an account/session
    // (see JoinSeason.vue), unlike every other seasons endpoint above.
    season_by_join_code(code){
        return api.request('GET', '/seasons/join/' + code, null, false);
    },
    join_season(code){
        return api.request('POST', '/seasons/join', { code: code });
    },
    // Staff-only, used from Panel.vue in either mode - authenticates with the
    // websocket session (game.session), not the online player token. Sends
    // the file in MEDIA_CHUNK_SIZE pieces (see server.js's
    // /api/media/uploads endpoints): each chunk is retried on network
    // errors, and a 409 tells us the offset the server actually has, so a
    // flaky connection resumes instead of starting over.
    //   options.onProgress(sent_bytes, total_bytes)
    //   options.signal - an AbortController signal, to cancel (the partial
    //   upload is discarded server-side too).
    async upload_media(file, session, options = {}){
        const headers = { 'Authorization': 'Bearer ' + session };
        const signal = options.signal;
        const call = async (method, path, body, extra_headers) => {
            const response = await fetch(api.base_url() + path, {
                method: method,
                headers: Object.assign({}, headers, extra_headers),
                body: body,
                signal: signal,
            });
            let result = {};
            try{ result = await response.json(); }catch(e){}
            return { status: response.status, ok: response.ok && result.ok == true, result: result };
        };

        const start = await call('POST', '/media/uploads', JSON.stringify({ filename: file.name, size: file.size }), { 'Content-Type': 'application/json' });
        if(!start.ok)
            throw new Error(start.result.message || 'No se ha podido iniciar la subida.');
        const upload_id = start.result.upload_id;

        try{
            let offset = 0;
            let failures = 0;
            while(offset < file.size){
                const chunk = file.slice(offset, Math.min(offset + MEDIA_CHUNK_SIZE, file.size));
                let res;
                try{
                    res = await call('PUT', '/media/uploads/' + upload_id + '?offset=' + offset, chunk, { 'Content-Type': 'application/octet-stream' });
                }catch(ex){
                    if(signal && signal.aborted) throw ex;
                    res = null;
                }

                if(res && (res.ok || res.status == 409) && typeof res.result.received == 'number'){
                    offset = res.result.received;
                    failures = 0;
                    if(options.onProgress) options.onProgress(offset, file.size);
                    continue;
                }
                if(res && res.status >= 400 && res.status < 500)
                    throw new Error(res.result.message || 'No se ha podido subir el archivo.');
                if(++failures > MEDIA_CHUNK_RETRIES)
                    throw new Error('Se ha perdido la conexión durante la subida.');
                await new Promise((resolve) => setTimeout(resolve, 1000 * failures));
            }

            const done = await call('POST', '/media/uploads/' + upload_id + '/complete');
            if(!done.ok)
                throw new Error(done.result.message || 'No se ha podido completar la subida.');
            return done.result.url;
        }catch(ex){
            // Best-effort cleanup - deliberately not tied to the (possibly
            // already aborted) signal.
            fetch(api.base_url() + '/media/uploads/' + upload_id, { method: 'DELETE', headers: headers }).catch(() => {});
            throw ex;
        }
    }
};

export default api;
